import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';

const Navigation = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { isArabic } = useLanguage();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/research', label: t('nav.research') },
    { path: '/notes', label: t('nav.notes') },
    { path: '/about', label: t('nav.about') },
    { path: '/cv', label: t('nav.cv') }
  ];

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle scroll visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // On desktop: Show nav when at top (first 100px)
      if (!isMobile) {
        if (currentScrollY < 100) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      } else {
        // On mobile: Always show hamburger unless menu is open
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isMobile]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  // Desktop Navigation
  if (!isMobile) {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.nav 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed top-8 left-8 right-8 z-40 flex items-center justify-between"
            aria-label="Main navigation"
            data-testid="main-navigation"
            style={{ direction: 'ltr' }}
          >
            {/* Language Toggle - Always on left */}
            <div style={{ direction: 'ltr' }}>
              <LanguageToggle />
            </div>
            
            {/* Nav Links */}
            <ul className="flex gap-8" style={{ direction: isArabic ? 'rtl' : 'ltr' }}>
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`nav-link font-mono text-sm uppercase tracking-[0.15em] transition-colors block ${
                        isActive 
                          ? 'font-bold text-[#8B6F47] border-b-2 border-[#8B6F47]' 
                          : 'font-normal text-[#1C1C1C] hover:text-[#8B6F47]'
                      }`}
                      data-testid={`nav-link-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    );
  }

  // Mobile Navigation with Hamburger Menu
  return (
    <>
      {/* Language Toggle - Fixed position on mobile */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-6 left-6 z-50"
      >
        <LanguageToggle />
      </motion.div>

      {/* Hamburger Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="fixed top-6 right-6 z-50 w-12 h-12 bg-transparent text-[#1C1C1C] flex items-center justify-center hover:text-[#8B6F47] transition-colors"
        aria-label="Toggle menu"
        data-testid="mobile-menu-button"
      >
        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </motion.button>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-[#FDFBF7] z-40"
            data-testid="mobile-menu"
          >
            <nav className="flex items-center justify-center min-h-screen px-6">
              <ul className="space-y-8 text-center">
                {navItems.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <motion.li
                      key={item.path}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="py-2"
                    >
                      <Link
                        to={item.path}
                        className={`mobile-nav-link font-cutive text-3xl md:text-4xl block transition-colors text-[#1C1C1C] hover:text-[#8B6F47] leading-normal ${
                          isActive ? 'font-bold underline decoration-2 underline-offset-8 decoration-[#8B6F47]' : ''
                        }`}
                        data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                      >
                        {item.label}
                      </Link>
                    </motion.li>
                  );
                })}
              </ul>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
